import { Dropdown } from "primereact/dropdown";
import { Editor } from "primereact/editor";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import "react-quill/dist/quill.snow.css";
import Http from "../../../Common/Http";

const Edit = ({ setVisible, fetchData, rowData, onSuccess }) => {
  const [inputs, setInputs] = useState({
    project_id: null,
    title: "",
    description: "",
    status: "To Do",
    developer_id: null,
    story_points: 0,
    label_id: null,
  });
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [labels, setLabels] = useState([]);

  // Status options
  const statusOptions = [
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Fetch projects, developers, and labels on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setFormLoading(true);
      setError("");
      let hasError = false;

      try {
        // Fetch projects
        const projectsRes = await Http.get("/project");
        if (projectsRes.data && projectsRes.data.data) {
          setProjects(
            projectsRes.data.data.map((project) => ({
              label: project.title,
              value: project.id,
            }))
          );
        } else {
          console.warn("No projects data found");
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        hasError = true;
      }

      try {
        const developersRes = await Http.get("/staff");

        if (Array.isArray(developersRes.data)) {
          setDevelopers(
            developersRes.data.map((dev) => ({
              label: dev.name || `${dev.first_name || ""} ${dev.last_name || ""}`.trim() || `ID: ${dev.id}`,
              value: dev.id,
            }))
          );
        } else {
          console.warn("No developers data found");
        }
      } catch (error) {
        console.error("Error fetching developers:", error);
        // Continue even if developers can't be fetched
      }

      try {
        // Fetch labels
        const labelsRes = await Http.get("/label");
        console.log("Labels data from edit:", labelsRes.data);
        if (labelsRes.data && labelsRes.data.data) {
          setLabels(
            labelsRes.data.data.map((label) => ({
              label: label.name,
              value: label.id,
              color: label.color,
            }))
          );
        } else {
          console.warn("No labels data found");
        }
      } catch (error) {
        console.error("Error fetching labels:", error);
        hasError = true;
      }

      if (hasError) {
        setError("Some form data could not be loaded. You may still edit the story but some options might be missing.");
      }

      setFormLoading(false);
    };

    fetchInitialData();
  }, []);

  // Initialize form with row data when available
  useEffect(() => {
    if (rowData) {
      setInputs({
        project_id: rowData.project_id,
        title: rowData.title || "",
        description: rowData.description || "",
        status: rowData.status || "To Do",
        developer_id: rowData.developer_id,
        story_points: rowData.story_points || 0,
        label_id: rowData.label_id,
      });
    }
  }, [rowData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (e) => {
    setInputs((prev) => ({ ...prev, description: e.htmlValue }));
  };

  const handleDropdownChange = (e, fieldName) => {
    setInputs((prev) => ({ ...prev, [fieldName]: e.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Form validation
    if (!inputs.project_id) {
      setError("Please select a project");
      setLoading(false);
      return;
    }

    if (!inputs.title.trim()) {
      setError("Please enter a title");
      setLoading(false);
      return;
    }

    try {
      // Create form data for API request
      const formData = {
        project_id: inputs.project_id,
        title: inputs.title.trim(),
        description: inputs.description,
        status: inputs.status,
        developer_id: inputs.developer_id,
        story_points: inputs.story_points,
        label_id: inputs.label_id,
      };

      const response = await Http.put(`/story/${rowData.id}`, formData);

      // If we have a response with data and an onSuccess callback is provided
      if (response.data) {
        if (onSuccess) {
          onSuccess(response.data.data || response.data);
        }
        // For backward compatibility
        else if (setVisible && fetchData) {
          setVisible(false);
          fetchData();
        }
      }
    } catch (error) {
      console.error("Error updating story:", error);
      setError(error.response?.data?.message || "Failed to update story. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Custom template for label items in dropdown
  const labelTemplate = (option) => {
    if (!option) return null;
    return (
      <div className="d-flex align-items-center">
        <div
          style={{
            width: "15px",
            height: "15px",
            backgroundColor: option.color || "#ccc",
            borderRadius: "3px",
            marginRight: "8px",
          }}
        ></div>
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <div>
      {loading || formLoading ? (
        <div className="text-center p-5">
          <ProgressSpinner />
          <p className="mt-2">Loading form data...</p>
        </div>
      ) : (
        <div className="container">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row mb-3">
              <div className="col-lg-12">
                <label htmlFor="project_id" className="form-label">
                  Project <span className="text-danger">*</span>
                </label>
                <Dropdown
                  id="project_id"
                  value={inputs.project_id}
                  options={projects}
                  onChange={(e) => handleDropdownChange(e, "project_id")}
                  placeholder="Select a project"
                  className="w-100"
                  required
                  emptyMessage="No projects available"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-lg-12">
                <label htmlFor="title" className="form-label">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  id="title"
                  value={inputs.title}
                  onChange={handleInputChange}
                  placeholder="Enter story title"
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-lg-12">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <Editor id="description" value={inputs.description} onTextChange={handleEditorChange} style={{ height: "200px" }} />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="status" className="form-label">
                  Status
                </label>
                <Dropdown
                  id="status"
                  value={inputs.status}
                  options={statusOptions}
                  onChange={(e) => handleDropdownChange(e, "status")}
                  placeholder="Select status"
                  className="w-100"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="developer_id" className="form-label">
                  Assigned Developer (Optional)
                </label>
                <Dropdown
                  id="developer_id"
                  value={inputs.developer_id}
                  options={developers}
                  onChange={(e) => handleDropdownChange(e, "developer_id")}
                  placeholder="Assign to developer"
                  className="w-100"
                  showClear
                  emptyMessage="No developers available"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="story_points" className="form-label">
                  Story Points
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="story_points"
                  id="story_points"
                  value={inputs.story_points}
                  onChange={handleInputChange}
                  min="0"
                />
                <small className="text-muted">This is automatically updated as tasks are added</small>
              </div>
              <div className="col-md-6">
                <label htmlFor="label_id" className="form-label">
                  Label (Optional)
                </label>
                <Dropdown
                  id="label_id"
                  value={inputs.label_id}
                  options={labels}
                  onChange={(e) => handleDropdownChange(e, "label_id")}
                  placeholder="Select a label"
                  className="w-100"
                  showClear
                  itemTemplate={labelTemplate}
                  valueTemplate={labelTemplate}
                  emptyMessage="No labels available"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12">
                <button type="submit" className="btn btn-primary float-end">
                  Update Story
                </button>
                <button type="button" className="btn btn-secondary float-end me-2" onClick={() => setVisible(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Edit;
